<script setup>
// Vídeo: link + capa (imagem com ▶ montada no servidor). YouTube/Vimeo ganham
// capa sozinhos ao sair do campo; outro site usa uma imagem da biblioteca.
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MediaLibraryPanel from 'dashboard/routes/dashboard/marketing/pages/MediaLibraryPanel.vue';
import { useAlert } from 'dashboard/composables';
import API from 'dashboard/api/marketingEmailTemplates';
import SpacingField from '../../fields/SpacingField.vue';
import SliderField from '../../fields/SliderField.vue';
import AlignField from '../../fields/AlignField.vue';
import VisibilityField from '../../fields/VisibilityField.vue';
import { withScheme } from '../../document/links';
import { videoProvider } from '../../document/video';

const props = defineProps({
  node: { type: Object, required: true },
  selection: { type: Object, default: null },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const media = ref(null);
const busy = ref(false);
const st = computed(() => props.node.style || {});
const width = computed(() => props.node.width || { value: 100, unit: '%' });
const style = (key, value) => emit('update', { style: { [key]: value } }, key);

// Link atual (o painel pode emitir antes de a prop voltar) e controle de corrida:
// só a última busca vale, e só se o link ainda for o mesmo de quando pediu.
const currentUrl = ref(props.node.url || '');
watch(
  () => props.node.url,
  v => {
    currentUrl.value = v || '';
  }
);
let seq = 0;
let focusUrl = null;
let lastRequestedUrl = null;
const setUrl = value => {
  currentUrl.value = value;
  emit('update', { url: value }, 'url');
};

const makeCover = async (url, assetId = null) => {
  seq += 1;
  const my = seq;
  lastRequestedUrl = url;
  busy.value = true;
  try {
    const { data } = await API.videoThumbnail(url, assetId);
    if (my !== seq || currentUrl.value !== url) return;
    emit(
      'update',
      { media: { asset_id: data.asset_id, url: data.url } },
      'media'
    );
  } catch (error) {
    if (my !== seq || currentUrl.value !== url) return;
    useAlert(
      error?.response?.data?.error || t('EMAIL_TEMPLATES.EDITOR.VIDEO.ERROR')
    );
  } finally {
    if (my === seq) busy.value = false;
  }
};
const onUrlFocus = () => {
  focusUrl = currentUrl.value;
};
// Link de YouTube/Vimeo novo (ou sem capa ainda) refaz a capa: a antiga era de outro vídeo.
const onUrlBlur = value => {
  const fixed = withScheme(value);
  if (fixed !== value) setUrl(fixed);
  const baseline = lastRequestedUrl ?? focusUrl;
  const changed = fixed !== baseline;
  if (videoProvider(fixed) && (changed || !props.node.media?.url)) {
    makeCover(fixed);
  }
};
const pick = asset => {
  media.value?.close();
  makeCover(currentUrl.value, asset.id);
};
</script>

<template>
  <div data-props="video" class="flex flex-col gap-4">
    <label data-field="video-url" class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.VIDEO.URL')
      }}</span>
      <input
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        :value="node.url"
        :placeholder="t('EMAIL_TEMPLATES.EDITOR.VIDEO.URL_PLACEHOLDER')"
        @focus="onUrlFocus"
        @input="setUrl($event.target.value)"
        @blur="onUrlBlur($event.target.value)"
      />
      <span class="text-[11px] text-n-slate-10">{{
        t('EMAIL_TEMPLATES.EDITOR.VIDEO.URL_HINT')
      }}</span>
    </label>
    <div class="flex flex-wrap items-center gap-2">
      <button
        type="button"
        data-test="make-cover"
        class="rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        :disabled="busy || !node.url"
        @click="makeCover(node.url)"
      >
        {{
          busy
            ? t('EMAIL_TEMPLATES.EDITOR.VIDEO.WORKING')
            : t('EMAIL_TEMPLATES.EDITOR.VIDEO.MAKE_COVER')
        }}
      </button>
      <button
        type="button"
        data-test="library-cover"
        class="rounded-lg border border-n-weak px-3 py-1.5 text-xs text-n-slate-12 disabled:opacity-50"
        :disabled="busy || !node.url"
        :title="
          node.url ? undefined : t('EMAIL_TEMPLATES.EDITOR.VIDEO.NEED_URL')
        "
        @click="media?.open()"
      >
        {{ t('EMAIL_TEMPLATES.EDITOR.VIDEO.LIBRARY_COVER') }}
      </button>
    </div>
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.VIDEO.WIDTH')"
      :model-value="width.value"
      :min="5"
      :max="100"
      :unit="t('EMAIL_TEMPLATES.EDITOR.PROPS.UNIT_PCT')"
      @update:model-value="
        v => emit('update', { width: { value: v, unit: '%' } }, 'width')
      "
    />
    <AlignField
      :model-value="node.align"
      @update:model-value="v => emit('update', { align: v }, 'align')"
    />
    <label class="flex flex-col gap-1.5">
      <span class="text-xs font-medium text-n-slate-11">{{
        t('EMAIL_TEMPLATES.EDITOR.VIDEO.ALT')
      }}</span>
      <input
        class="rounded-lg border border-n-weak bg-n-solid-1 px-2 py-1.5 text-sm"
        maxlength="200"
        :value="node.alt"
        @input="emit('update', { alt: $event.target.value }, 'alt')"
      />
    </label>
    <SliderField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.RADIUS')"
      :model-value="st.radius || 0"
      :min="0"
      :max="60"
      unit="px"
      @update:model-value="v => style('radius', v)"
    />
    <SpacingField
      :label="t('EMAIL_TEMPLATES.EDITOR.PROPS.PADDING')"
      :model-value="st.padding"
      @update:model-value="v => style('padding', v)"
    />
    <VisibilityField
      v-if="selection?.row?.role !== 'footer'"
      :model-value="st.hide_on"
      @update:model-value="v => style('hide_on', v)"
    />
    <Dialog
      ref="media"
      :title="t('EMAIL_TEMPLATES.EDITOR.VIDEO.LIBRARY_COVER')"
      :show-confirm-button="false"
      width="3xl"
      overflow-y-auto
    >
      <MediaLibraryPanel pickable @pick="pick" />
    </Dialog>
  </div>
</template>
